import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { HandleTokenService } from '../../../core/services/handle-token.service';
import { AppFacade } from '../../../facades/app.facade';
import { ErrorFacade } from '../../../facades/error.facade';
import { MapErrorService } from '../../services/error/map-error.service';

import { Store } from '@ngrx/store';
import {
  catchError,
  delay,
  map,
  mergeMap,
  switchMap,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { APP_ROUTES as ROUTES } from '../../../app.routes';
import { UtilService } from '../../../services/util.service';

import { APP_CONSTANTS as CONST } from '@app/app.constants';
import { environment as ENV } from '@environment';
import * as appSelectors from '@store/app/app.selectors';
import * as customerActions from '@store/customer/customer.actions';
import * as customerSelectors from '@store/customer/customer.selectors';
import { interval, of } from 'rxjs';
import { EVALIDATION_STATUS } from '../../../core/enums/customer.enum';
import { IExtraData, ILargeContentConfig, IPutRecord } from '../../../core/models/app.model';
import { ICustomerValidate, IPreLoan } from '../../../core/models/customer.model';
import { IPaymentParams } from '../../../core/models/payments.model';
import { CustomerAPI } from '../../../data/mo-api/customer.api';
import { CustomerFacade } from '../../../facades/customer.facade';
import { PaymentsFacade } from '../../../facades/payments.facade';
import { UIService } from '../../../services/ui.service';
import { Customer } from '../../classes/customer.class';
import { EVENTS } from '../../../data/events/events-by-category';
import { CustomerService } from '../../services/customer/customer.service';
import { Title } from '@angular/platform-browser';
import { ERROR_COMPONENT_CONSTANTS as ERROR } from '@app/domain/services/error/error.constants';
import { EERROR_PAGE_TYPES } from '@core/models/error.model';
import { AppService } from '../../services/app.service';

@Injectable()
export class CustomerEffects {
  constructor(
    private actions$: Actions,
    private _handleTokenService: HandleTokenService,
    private _utilService: UtilService,
    private _store: Store,
    private _customerAPI: CustomerAPI,
    private _customerFacade: CustomerFacade,
    private _errorFacade: ErrorFacade,
    private _mapErrorService: MapErrorService,
    private _uiService: UIService,
    private _appFacade: AppFacade,
    private _paymentFacade: PaymentsFacade,
    private _customerService: CustomerService,
    private _titleService: Title,
    private _appService: AppService
  ) {}

  public requestCustomerInformation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerActions.requestCustomerInformation),
      switchMap((_) =>
        this._customerAPI.requestCustomerInformation().pipe(
          map((customer) => {
            return customerActions.successRequestCustomerInformation({
              customer: new Customer(customer),
            });
          }),
          catchError((error) => of(customerActions.failureRequestCustomerInformation({ error })))
        )
      )
    )
  );

  public successRequestCustomerInformation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.successRequestCustomerInformation),
        withLatestFrom(this._store.select(appSelectors.selectStep)),
        tap(([_, step]) => {
          this._handleTokenService.setUserStep(step);
        })
      ),
    { dispatch: false }
  );

  public failureRequestCustomerInformation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.failureRequestCustomerInformation),
        tap(({ error }) => {
          const message = this._mapErrorService.mapError(error);
          this._errorFacade.handleError(message);
        })
      ),
    { dispatch: false }
  );

  public listenUserScore$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.listenUserScore),
        withLatestFrom(this._store.select(appSelectors.selectTransactions)),
        tap(([_, { id }]) => {
          return interval(CONST.TIME_SCORE_LOOP)
            .pipe(
              mergeMap(() => this._customerAPI.requestUserScore(id)),
              tap((validation: ICustomerValidate) => {
                if (!this._customerService.handleCustomerValidation(validation)) return false;
                const data: IPutRecord = EVENTS.KYC.REQUEST;
                const extraData: IExtraData = { request: { id }, response: validation };
                this._appFacade.sendPutRecord(data, extraData);
                return this._customerFacade.successListenUserScore(validation);
              }),
              takeWhile(
                ({ validation_status }) => validation_status === EVALIDATION_STATUS.PENDING
              ),
              catchError((error) => {
                const data: IPutRecord = EVENTS.KYC.REQUEST;
                const extraData: IExtraData = {
                  request: { id },
                  response: { error: error?.error ?? error },
                };
                this._appFacade.sendPutRecord(data, extraData);
                return of(this._appFacade.killPurchase());
              })
            )
            .subscribe();
        })
      ),
    { dispatch: false }
  );

  public successListenUserScore$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.successListenUserScore),
        withLatestFrom(this._store.select(appSelectors.selectSession)),
        tap(([{ validation }, session]) => {
          const { is_customer } = session;
          const {
            has_card,
            validation_status,
            can_request_validator,
            can_request_loan,
            can_aggregator_capacity,
          } = validation;
          const errorData = ERROR[EERROR_PAGE_TYPES.ERROR_10];
          const errorContent = ERROR[EERROR_PAGE_TYPES.ERROR_01];
          const errorAggregatorCapacity = ERROR[EERROR_PAGE_TYPES.ERROR_19];
          const { error } = this._appService.handleCustomerSession(validation, session);
          if (error && validation_status === EVALIDATION_STATUS.ERROR && !can_request_validator) {
            this._appFacade.killPurchaseWithoutRedirect('status-error', is_customer, errorContent);
            return;
          }
          if (validation_status === EVALIDATION_STATUS.ERROR && !!can_request_validator) {
            return this._store.dispatch(customerActions.listenUserScore());
          }
          if (error && validation_status !== EVALIDATION_STATUS.ERROR && !can_aggregator_capacity) {
            this._appFacade.killPurchaseWithoutRedirect(
              'can-aggregator-capacity',
              is_customer,
              errorAggregatorCapacity
            );
            return;
          }
          if (
            error &&
            validation_status !== EVALIDATION_STATUS.ERROR &&
            !!can_aggregator_capacity
          ) {
            this._appFacade.killPurchaseWithoutRedirect(
              'has-payment-capacity',
              is_customer,
              errorData
            );
            return;
          }

          const nextRoute: string = ROUTES.SCORE;
          this._utilService.navigateTo(nextRoute);
          return has_card ? this._customerFacade.getCustomerCard() : false;
        })
      ),
    { dispatch: false }
  );

  public redirectUserToScore$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.redirectUserToScore),
        delay(10000),
        tap((_) => {
          const route: string = ROUTES.SCORE;
          this._utilService.navigateTo(route);
        })
      ),
    { dispatch: false }
  );

  public requestPreLoan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerActions.requestPreLoan),
      withLatestFrom(this._store.select(appSelectors.selectTransactions)),
      switchMap(([_, { amount }]) =>
        // TODO: Remove/Fix first parameter in the moment of integrate de new loan services
        this._customerAPI.requestPreLoan('012345', amount).pipe(
          map((preLoan) => {
            const data: IPutRecord = EVENTS.CREDIT.REQUEST_PRELOAN;
            const extraData: IExtraData = { request: { amount }, response: preLoan };
            this._appFacade.sendPutRecord(data, extraData);
            const event: IPutRecord = EVENTS.CREDIT.EVENT;
            this._appFacade.sendPutRecord(event);
            return customerActions.successRequestPreLoan({ preLoan });
          }),
          catchError((error) => {
            const data: IPutRecord = EVENTS.CREDIT.REQUEST_PRELOAN;
            const extraData: IExtraData = {
              request: { amount },
              response: { error: error?.error ?? error },
            };
            this._appFacade.sendPutRecord(data, extraData);
            return of(customerActions.failureRequestPreLoan({ error }));
          })
        )
      )
    )
  );

  public successRequestPreLoan$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.successRequestPreLoan),
        tap((_) => this._appFacade.setIsLoading()),
        delay(50),
        tap((_) => {
          const isLargeContent: ILargeContentConfig = this._uiService.isLargeContent();
          this._appFacade.setLargeContentConfig(isLargeContent);
        })
      ),
    { dispatch: false }
  );

  public failureRequestPreLoan$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.failureRequestPreLoan),
        delay(50),
        tap((_) => {
          const isLargeContent: ILargeContentConfig = this._uiService.isLargeContent();
          this._appFacade.setLargeContentConfig(isLargeContent);
        })
      ),
    { dispatch: false }
  );

  public createPreLoan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerActions.createPreLoan),
      withLatestFrom(this._store.select(appSelectors.selectTransactions)),
      delay(500),
      switchMap(([{ createPreLoan }, { others }]) => {
        this._titleService.setTitle('Transición a pago');
        const event: IPutRecord = EVENTS.CREDIT.EVENT;
        this._appFacade.sendPutRecord(event);
        return !others?.pre_loan_id
          ? this._customerAPI.createPreLoans(createPreLoan).pipe(
              map((preLoan: IPreLoan) => {
                const data: IPutRecord = EVENTS.CREDIT.REQUEST_PRELOAN;
                const extraData: IExtraData = { request: createPreLoan, response: preLoan };
                this._appFacade.sendPutRecord(data, extraData);
                return customerActions.successCreatePreLoan(preLoan);
              }),
              catchError((error) => of(customerActions.failureCreatePreLoan({ error })))
            )
          : of(customerActions.successCreatePreLoan({ id: others?.pre_loan_id }));
      })
    )
  );

  public successCreatePreLoan$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.successCreatePreLoan),
        withLatestFrom(
          this._store.select(customerSelectors.selectCustomerValidation),
          this._store.select(customerSelectors.selectCustomerCard)
        ),
        tap(([_, has_card, cardInfo]) => {
          const deviceData: string = this._paymentFacade.getDeviceData();
          const paymentParams: IPaymentParams = {
            device_session_id: deviceData,
          };

          const hasLoanWithCard = !has_card || !cardInfo;

          if (hasLoanWithCard) this._handlePaymentIfNotHasCard();

          const isLoadingPreLoan = false;

          return hasLoanWithCard
            ? this._customerFacade.updateIsLoadingPreLoan(isLoadingPreLoan)
            : this._paymentFacade.createPaymentsIfHasCard(paymentParams);
        })
      ),
    { dispatch: false }
  );

  private _setISODate(): string {
    return this._utilService.getISODate();
  }

  private _handlePaymentIfNotHasCard = () => {
    const route: string = ROUTES.PAYMENT_METHOD;
    this._utilService.navigateTo(route);
    this._appFacade.nextStep();
  };

  public failureCreatePreLoan$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.failureCreatePreLoan),
        tap(({ error }) => {
          const message = this._mapErrorService.mapError(error);
          this._errorFacade.handleError(message);
          const route: string = ROUTES.ERROR;
          this._utilService.navigateTo(route);
        })
      ),
    { dispatch: false }
  );

  public redirectToEcommerce$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.redirectToEcommerce),
        withLatestFrom(this._store.select(customerSelectors.selectRedirectUrl)),
        tap(([{ portal }, redirectUrl]) => {
          return portal
            ? this._redirectToPortal(redirectUrl)
            : this._redirectToEcommerce(redirectUrl);
        })
      ),
    { dispatch: false }
  );

  private _redirectToPortal = (redirectUrl: string) => {
    const portalRoute: string = ENV.portal_url;
    this._closeSession();
    this._utilService.onOpenLink(portalRoute);
    this._utilService.redirectToUrl(redirectUrl);
  };

  private _redirectToEcommerce = (redirectUrl: string) => {
    this._closeSession();
    this._utilService.redirectToUrl(redirectUrl);
  };

  private _closeSession = () => {
    this._handleTokenService.removeAccessToken();
    this._handleTokenService.cancelTransactionId();
    this._handleTokenService.removeUserStep();
    this._handleTokenService.removeTransactionMinMax();
  };

  public getCustomerCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerActions.getCustomerCard),
      switchMap((_) =>
        this._customerAPI.getCustomerCard().pipe(
          map((dataResponseCard) => {
            const had_valid_loan = dataResponseCard.had_valid_loan;
            const cardInfo = had_valid_loan ? dataResponseCard : null;
            return customerActions.successGetCustomerCard({ cardInfo });
          }),
          catchError((error) => of(customerActions.failureGetCustomerCard({ error })))
        )
      )
    )
  );

  public successGetCustomerCard$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.successGetCustomerCard),
        tap((_) => {
          const deviceSessionId: string = this._paymentFacade.getDeviceData();
          return this._paymentFacade.setDeviceSessionId(deviceSessionId);
        })
      ),
    { dispatch: false }
  );
}
